import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useApiQuery } from '../../../../../../api/hooks';
import apiClient from '../../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../../stores';
import { Endpoints, Defaults, QueryKey, BusinessErrorCode } from '../../../../../../views/democracy/constants';
import { extractErrorMessage } from '../../../../../../views/democracy/utils';
import { PlusIcon } from '../../../../model/icons';
import RoundItem from './RoundItem';
import RoundEditModal from './RoundEditModal';
import './questions.css';

// Под-таб «Вопросы»: раунды (раскрытие) + таблица вопросов в каждом.
export default function QuestionsPanel({ democracy }) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();

    const roundsKey = QueryKey.rounds(democracy.id);
    const { data: rawRounds = [] } = useApiQuery(
        roundsKey,
        Endpoints.rounds.listForDemocracy(democracy.id),
        { refetchOnMount: 'always' }
    );
    const rounds = (rawRounds || []).filter(r => r && r.id != null).sort((a, b) => (a.orderId || 0) - (b.orderId || 0));

    // Число вопросов по раундам берём из полной демократии (список раундов их не отдаёт).
    const fullKey = QueryKey.democracyFull(democracy.id);
    const { data: full } = useApiQuery(fullKey, Endpoints.democracies.get(democracy.id), { refetchOnMount: 'always' });
    const questionCounts = useMemo(() => {
        const map = {};
        (full?.rounds || []).forEach(r => { if (r && r.id != null) map[r.id] = (r.questions || []).length; });
        return map;
    }, [full]);

    const [expandedIds, setExpandedIds] = useState(() => new Set());
    const [editingRoundId, setEditingRoundId] = useState(null);

    const invalidateRounds = () => {
        queryClient.invalidateQueries({ queryKey: roundsKey });
        queryClient.invalidateQueries({ queryKey: fullKey });
    };

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const [localRoundOrder, setLocalRoundOrder] = useState(null);
    const orderedRounds = localRoundOrder
        ? localRoundOrder.map(id => rounds.find(r => r.id === id)).filter(Boolean)
        : rounds;

    const handleRoundDragEnd = async ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const ids = orderedRounds.map(r => r.id);
        const newIds = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
        setLocalRoundOrder(newIds);
        show();
        try {
            const resp = await apiClient.put(Endpoints.rounds.reorder(democracy.id), { orderedRoundIds: newIds });
            if (Array.isArray(resp.data)) queryClient.setQueryData(roundsKey, resp.data);
            setLocalRoundOrder(null);
            enqueueSnackbar('Порядок раундов сохранён', { variant: 'success' });
        } catch (error) {
            setLocalRoundOrder(null);
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось изменить порядок раундов'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const toggleExpanded = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleAddRound = async () => {
        show();
        try {
            const fresh = await apiClient.get(Endpoints.rounds.listForDemocracy(democracy.id));
            const existing = Array.isArray(fresh.data) ? fresh.data : [];
            const orderId = existing.reduce((m, r) => Math.max(m, (r && r.orderId) || 0), 0) + 1;
            const response = await apiClient.post(Endpoints.rounds.createForDemocracy(democracy.id), {
                orderId, title: `Раунд ${orderId}`, description: null, timeBudgetSeconds: Defaults.timeBudgetSeconds
            });
            invalidateRounds();
            const createdId = response?.data?.id;
            if (createdId != null) setExpandedIds(prev => new Set(prev).add(createdId));
            enqueueSnackbar(`Раунд №${orderId} создан`, { variant: 'success' });
        } catch (error) {
            if (error?.response?.data?.code === BusinessErrorCode.ORDER_ID_TAKEN) {
                enqueueSnackbar('Нумерация устарела, перезагрузите страницу', { variant: 'warning' });
            } else {
                enqueueSnackbar(extractErrorMessage(error, 'Не удалось создать раунд'), { variant: 'error' });
            }
        } finally {
            hide();
        }
    };

    const editingRound = rounds.find(r => r.id === editingRoundId) || null;

    return (
        <>
            <div className="sec-head">
                <div className="left">
                    <span className="kicker">Раунды</span>
                    <span className="count">порядок проведения</span>
                </div>
                <button type="button" className="btn btn-primary" onClick={handleAddRound}>
                    <span className="ic"><PlusIcon /></span>Добавить раунд
                </button>
            </div>

            {rounds.length === 0 ? (
                <div className="round-empty">Ещё нет раундов. Нажмите «Добавить раунд».</div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRoundDragEnd}>
                    <SortableContext items={orderedRounds.map(r => r.id)} strategy={verticalListSortingStrategy}>
                        <div className="round-list">
                            {orderedRounds.map(round => (
                                <SortableRoundItem
                                    key={round.id}
                                    round={round}
                                    questionCount={questionCounts[round.id] ?? 0}
                                    expanded={expandedIds.has(round.id)}
                                    onToggle={() => toggleExpanded(round.id)}
                                    onEditRound={() => setEditingRoundId(round.id)}
                                    onChanged={invalidateRounds}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {editingRound && (
                <RoundEditModal
                    key={editingRound.id}
                    round={editingRound}
                    onChanged={invalidateRounds}
                    onClose={() => setEditingRoundId(null)}
                />
            )}
        </>
    );
}

function SortableRoundItem(props) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.round.id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
    return (
        <div ref={setNodeRef} style={style}>
            <RoundItem {...props} dragHandleProps={{ ...attributes, ...listeners }} />
        </div>
    );
}

SortableRoundItem.propTypes = {
    round: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
    questionCount: PropTypes.number
};

QuestionsPanel.propTypes = {
    democracy: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired
};
